import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';
import { registerErrorMessage, isOkCommand, parseResponse } from '@skynet/data-ws-core';
import type { TradingApiKeyItem, TradingApiKeysState } from './types';
import { TradingApiCommands } from './types';
import { actionEsOnMessage } from '@skynet/data-ws-es';
import { describeWsFailure } from 'src/store/utils';
import { createApiKey, deleteApiKey, listen, updateApiKey } from './thunks';
import { ES_BOOTSTRAP_LISTEN_SECTION } from 'src/store/trading/listen-sections';
import { AuthCommands } from 'src/store/auth/types';

// The two ES login failures are read here as well: until the socket is authed
// the init listen never goes out, so they are the only answer the bootstrap
// will ever get (see the first branch of `actionEsOnMessage`).
const allowedCommands: string[] = [
  ...Object.values(TradingApiCommands),
  AuthCommands.Login,
  AuthCommands.NotConnected,
];

// The investor list is delivered over the `Trader` command, so its failures are
// registered against that command (the old errors.ts keyed the dead string
// 'Investor', which never matched and therefore never surfaced an error).
registerErrorMessage(TradingApiCommands.Trader, {
  title: 'Investor Error',
  getDescription: describeWsFailure,
});

// The supported-exchange list is the other half of the ES Dashboard bootstrap.
// It had no registered message, so `onError` returned `null`, the slice's error
// branch never ran and a failure was silently dropped — leaving the page on a
// spinner with nothing on screen to explain it (SKY-4581).
registerErrorMessage(TradingApiCommands.SupportedExchanges, {
  title: 'Supported Exchanges Error',
  getDescription: describeWsFailure,
});

registerErrorMessage(TradingApiCommands.CreateESAPIKey, {
  title: 'Add API Key Error',
  getDescription: describeWsFailure,
});

registerErrorMessage(TradingApiCommands.UpdateESAPIKey, {
  title: 'Update API Key Error',
  getDescription: describeWsFailure,
});

registerErrorMessage(TradingApiCommands.DeleteESAPIKey, {
  title: 'Delete API Key Error',
  getDescription: describeWsFailure,
});

registerErrorMessage(TradingApiCommands.Token, {
  title: 'Token Error',
  getDescription: describeWsFailure,
});

export const initialState: TradingApiKeysState = {
  error: null,
  requestStatus: null,
  resAddAPIKeyStatus: null,
  resUpdateAPIKeyStatus: null,
  resDeleteAPIKeyStatus: null,
  isLoadingTickers: false,
  targetAccount: null,
  list: [],
  isLoadingInvestor: null,
  instruments: [],
  selectedExchange: undefined,
  supportedExchanges: [],
  supportedExchangesStatus: null,
};

const tradingApiKeysSlice = createSlice({
  name: 'tradingApiKeys',
  initialState,
  reducers: {
    onSelectedExchange: (state, action: PayloadAction<number | undefined>) => {
      state.selectedExchange = action.payload;
    },

    /*  setActiveListenSection: (state, action: PayloadAction<string | null>) => {
      state.activeListenSection = action.payload;
    }, */
    setSelectedApiKey: (state, action: PayloadAction<string | null>) => {
      state.targetAccount = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(listen, (state, action) => {
      const { section } = action.payload;
      const { isInitCall } = action.meta;
      if (isInitCall) {
        if (section === ES_BOOTSTRAP_LISTEN_SECTION) {
          // Only an unresolved bootstrap goes back to `pending`. Re-entering the
          // ES Dashboard re-sends this init listen for data we already hold, and
          // downgrading a resolved status would make `isLoadingEs` gate the page
          // again — unmounting the whole tree behind a blank spinner on every
          // navigation (SKY-4581). Refreshes now revalidate in the background.
          if (state.supportedExchangesStatus !== 'success') {
            state.supportedExchangesStatus = 'pending';
          }
          if (state.isLoadingInvestor !== 'success') {
            state.isLoadingInvestor = 'pending';
          }
        }
        state.requestStatus = 'pending';
      }
    });

    builder.addCase(createApiKey.pending, (state) => {
      state.resAddAPIKeyStatus = 'pending';
    });
    builder.addCase(updateApiKey.pending, (state) => {
      state.resUpdateAPIKeyStatus = 'pending';
    });
    builder.addCase(deleteApiKey.pending, (state) => {
      state.resDeleteAPIKeyStatus = 'pending';
    });

    builder.addCase(actionEsOnMessage, (state, action) => {
      const { json, skipProcessing, error, okCommand, errorCommand } = parseResponse(
        action.payload,
        allowedCommands,
      );

      if (skipProcessing || !json) return;

      const { command } = json;

      // The ES login failed, or the server says the account has no ES access.
      // Either way the socket stays connected but never authed, `useListen`
      // never sends the init listen, and the bootstrap statuses sit at `null`
      // for good — which `isLoadingEs` reads as "still loading", so Tasks would
      // spin with nothing on screen to explain it. Settle whatever has not
      // resolved as failed so the pages' error states take over; a later
      // successful login re-sends the init listen, which re-arms it (SKY-4581).
      // A bootstrap that already succeeded keeps its data on screen.
      if (errorCommand === AuthCommands.Login || command === AuthCommands.NotConnected) {
        if (state.isLoadingInvestor !== 'success') {
          state.isLoadingInvestor = 'error';
        }
        if (state.supportedExchangesStatus !== 'success') {
          state.supportedExchangesStatus = 'error';
        }
        return;
      }

      // A failed bootstrap command has to settle its own status. `isLoadingEs`
      // and `TradingApiKeys` both wait for these two to leave `pending`, so
      // without this they would wait for a `success` that is never coming and
      // the page would spin forever (SKY-4581). Keyed on `errorCommand` so an
      // unrelated failure — a rejected key deletion, say — cannot mark the
      // bootstrap as failed, and placed ahead of the `error` branch so it does
      // not depend on the command having a registered error message.
      if (errorCommand === TradingApiCommands.Trader) {
        state.isLoadingInvestor = 'error';
      }
      if (errorCommand === TradingApiCommands.SupportedExchanges) {
        state.supportedExchangesStatus = 'error';
      }

      if (error) {
        state.error = error;
        state.requestStatus = 'error';
        state.resAddAPIKeyStatus = 'error';
        state.resUpdateAPIKeyStatus = 'error';
        state.resDeleteAPIKeyStatus = 'error';

        return;
      }

      if (isOkCommand(command)) {
        if (okCommand === TradingApiCommands.CreateESAPIKey) {
          state.resAddAPIKeyStatus = 'success';
        }
        if (okCommand === TradingApiCommands.UpdateESAPIKey) {
          state.resUpdateAPIKeyStatus = 'success';
        }
        if (okCommand === TradingApiCommands.DeleteESAPIKey) {
          state.resDeleteAPIKeyStatus = 'success';
        }
        return;
      }

      if (command === TradingApiCommands.SupportedExchanges) {
        const { exchanges } = json as { exchanges: number[] };
        state.supportedExchanges = exchanges;
        state.supportedExchangesStatus = 'success';
      }

      if (command === TradingApiCommands.Trader) {
        const { list } = json as { list: TradingApiKeyItem[] };
        state.list = list;
        state.isLoadingInvestor = 'success';
        state.resAddAPIKeyStatus = null;
      }
    });
  },
});

export const tradingApiKeysActions = tradingApiKeysSlice.actions;
export default tradingApiKeysSlice.reducer;
